import { lazy, ReactElement, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Header from '@containers/header/Header';
import SideNavBar from '@containers/side-nav-bar/SideNavBar';
import { useGetProfileQuery } from '@containers/auth/store/api';
import { useAppDispatch } from '@store/store';
import { updateProfile } from '@containers/auth/store/reducer';
import { Breadcrumbs } from '@components';

import { useLazyGetAllPermissionsOfUserQuery } from '@containers/users/store/api';
import { updateUserPermissions } from '@containers/users/store/reducer';
import { UserPermisssions } from '@constants/common';
import PermissionRoute from './PermissionRoute';
import RoutePaths from './RoutesPath';

const PageNotFound = lazy(() => import('@pages/PageNotFound'));
const AdminsPage = lazy(() => import('@pages/AdminsPage'));
const AdminDetailsPage = lazy(() => import('@pages/AdminDetailsPage'));
const UsersPage = lazy(() => import('@pages/UsersPage'));
const UserDetailsPage = lazy(() => import('@pages/UserDetailsPage'));
const DevicesPage = lazy(() => import('@pages/DevicesPage'));
const DeviceDetailsPage = lazy(() => import('@pages/DeviceDetailsPage'));
const RoutinesPage = lazy(() => import('@pages/RoutinesPage'));
const AmbientTemperaturePage = lazy(
  () => import('@pages/AmbientTemperaturePage')
);
const GeoFencingPage = lazy(() => import('@pages/GeoFencingPage'));
const HoursOfUsagePage = lazy(() => import('@pages/HoursOfUsagePage'));
const FilterResetInsightsPage = lazy(
  () => import('@pages/FilterResetInsightsPage')
);
const FeedBacksPage = lazy(() => import('@pages/FeedBacksPage'));
const FeedBackDetailsPage = lazy(() => import('@pages/FeedBackDetailsPage'));
const PushNotificationsPage = lazy(() => import('@pages/NotificationsPage'));
const PushNotificationDetailsPage = lazy(
  () => import('@pages/NotificationDetailsPage')
);
const UserExportHistoryPage = lazy(
  () => import('@pages/UserExportHistoryPage')
);

const DeviceExportHistoryPage = lazy(
  () => import('@pages/DeviceExportHistoryPage')
);

const FeedbackExportHistoryPage = lazy(
  () => import('@pages/FeedbackExportHistoryPage')
);

const WarrantyPage = lazy(() => import('@pages/WarrantyPage'));
const WarrantyDetailsPage = lazy(() => import('@pages/WarrantyDetailsPage'));
const WarrantyImportPage = lazy(() => import('@pages/WarrantyImportPage'));
const WarrantyHistoryPage = lazy(() => import('@pages/WarrantyHistoryPage'));
const AmbientTemperatureExportHistoryPage = lazy(
  () => import('@pages/AmbientTemperatureExportHistoryPage')
);
const UsagePatternExportHistoryPage = lazy(
  () => import('@pages/UsagePatternExportHistoryPage')
);

const PrivateLayout = () => {
  const [isBarExpanded, setBarExpanded] = useState(true);

  const dispatch = useAppDispatch();

  const { data: profileData } = useGetProfileQuery();
  const [
    getAllPermissionsOfUsers,
    { data: userPermissionsData, isError: isPermissionsError },
  ] = useLazyGetAllPermissionsOfUserQuery();

  useEffect(() => {
    getAllPermissionsOfUsers();
  }, []);

  useEffect(() => {
    if (userPermissionsData?.getAllPermissionsOfUser) {
      dispatch(
        updateUserPermissions(userPermissionsData?.getAllPermissionsOfUser)
      );
    }
  }, [userPermissionsData?.getAllPermissionsOfUser]);

  useEffect(() => {
    if (isPermissionsError) dispatch(updateUserPermissions([]));
  }, [dispatch, isPermissionsError]);

  useEffect(() => {
    if (profileData) {
      dispatch(
        updateProfile({
          ...profileData.getProfile,
        })
      );
    }
  }, [profileData]);

  const toggleSideNavBar = () => setBarExpanded(!isBarExpanded);
  const requirePermission = (
    permission: UserPermisssions,
    element: ReactElement
  ) => <PermissionRoute permission={permission}>{element}</PermissionRoute>;

  return (
    <>
      <SideNavBar isBarExpanded={isBarExpanded} />
      <div
        className={`${
          isBarExpanded
            ? 'ml-[var(--expanded-sidenav-width)] w-[calc(100vw-var(--expanded-sidenav-width))]'
            : 'ml-[var(--collapsed-sidenav-width)] w-[calc(100vw-var(--collapsed-sidenav-width))]'
        } h-screen transition-all duration-[var(--sidenav-animation-duration)] motion-reduce:transition-none`}
      >
        <Header
          isBarExpanded={isBarExpanded}
          toggleSideNavBar={toggleSideNavBar}
        />
        <div className='page mt-[80px]' id='main-container'>
          <div>
            <Breadcrumbs />
          </div>
          <Suspense>
            <Routes>
              <Route
                path={RoutePaths.LOGIN}
                element={<Navigate to={RoutePaths.USERS} replace />}
              />
              <Route
                path={RoutePaths.ADMINS}
                element={requirePermission(
                  UserPermisssions.VIEW_ADMINS,
                  <AdminsPage />
                )}
              />
              <Route
                path={RoutePaths.ADMIN_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_ADMINS,
                  <AdminDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.ADMINS_CREATE}
                element={requirePermission(
                  UserPermisssions.CREATE_ADMINS,
                  <AdminDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.USERS}
                element={requirePermission(
                  UserPermisssions.VIEW_USERS,
                  <UsersPage />
                )}
              />
              <Route
                path={RoutePaths.DEVICES}
                element={requirePermission(
                  UserPermisssions.VIEW_DEVICES,
                  <DevicesPage />
                )}
              />
              <Route
                path={RoutePaths.REPORTS.ROOT}
                element={<Navigate to={RoutePaths.REPORTS.ROUTINES} replace />}
              />
              <Route
                path={RoutePaths.REPORTS.ROUTINES}
                element={requirePermission(
                  UserPermisssions.VIEW_ROUTINES,
                  <RoutinesPage />
                )}
              />
              <Route
                path={RoutePaths.REPORTS.AVG_AMBIENT_TEMP}
                element={requirePermission(
                  UserPermisssions.VIEW_INSIGHTS,
                  <AmbientTemperaturePage />
                )}
              />
              <Route
                path={RoutePaths.REPORTS.GEOFENCING}
                element={requirePermission(
                  UserPermisssions.VIEW_GEOFENCES,
                  <GeoFencingPage />
                )}
              />
              <Route
                path={RoutePaths.REPORTS.HOURS_OF_USAGE}
                element={requirePermission(
                  UserPermisssions.VIEW_INSIGHTS,
                  <HoursOfUsagePage />
                )}
              />
              <Route
                path={RoutePaths.REPORTS.FILTER_RESET}
                element={requirePermission(
                  UserPermisssions.VIEW_INSIGHTS,
                  <FilterResetInsightsPage />
                )}
              />
              <Route
                path={RoutePaths.HOME}
                element={<Navigate to={RoutePaths.USERS} replace />}
              />
              <Route
                path={RoutePaths.USER_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_USER,
                  <UserDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.USER_EXPORT_HISTORY}
                element={requirePermission(
                  UserPermisssions.VIEW_EXPORT_HISTORY,
                  <UserExportHistoryPage />
                )}
              />
              <Route
                path={RoutePaths.DEVICE_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_DEVICE,
                  <DeviceDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.USER_DEVICE_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_DEVICE,
                  <DeviceDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.AMBIENT_TEMPERATURE_EXPORT_HISTORY}
                element={requirePermission(
                  UserPermisssions.VIEW_EXPORT_HISTORY,
                  <AmbientTemperatureExportHistoryPage />
                )}
              />
              <Route
                path={RoutePaths.USAGE_PATTERN_EXPORT_HISTORY}
                element={requirePermission(
                  UserPermisssions.VIEW_EXPORT_HISTORY,
                  <UsagePatternExportHistoryPage />
                )}
              />
              <Route
                path={RoutePaths.DEVICE_EXPORT_HISTORY}
                element={requirePermission(
                  UserPermisssions.VIEW_EXPORT_HISTORY,
                  <DeviceExportHistoryPage />
                )}
              />
              <Route
                path={RoutePaths.FEEDBACK}
                element={requirePermission(
                  UserPermisssions.VIEW_ALL_FEEDBACKS,
                  <FeedBacksPage />
                )}
              />
              <Route
                path={RoutePaths.FEEDBACK_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_FEEDBACK,
                  <FeedBackDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.FEEDBACK_EXPORT_HISTORY}
                element={requirePermission(
                  UserPermisssions.VIEW_EXPORT_HISTORY,
                  <FeedbackExportHistoryPage />
                )}
              />
              <Route
                path={RoutePaths.WARRANTY}
                element={requirePermission(
                  UserPermisssions.VIEW_DEVICE,
                  <WarrantyPage />
                )}
              />
              <Route
                path={RoutePaths.WARRANTY_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_WARRANTY_CONFIGS,
                  <WarrantyDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.WARRANTY_IMPORT}
                element={requirePermission(
                  UserPermisssions.CREATE_IMPORT,
                  <WarrantyImportPage />
                )}
              />
              <Route
                path={RoutePaths.WARRANTY_HISTORY}
                element={requirePermission(
                  UserPermisssions.VIEW_IMPORT_HISTORY,
                  <WarrantyHistoryPage />
                )}
              />
              <Route
                path={RoutePaths.PUSH_NOTIFICATIONS}
                element={requirePermission(
                  UserPermisssions.VIEW_CAMPAIGNS,
                  <PushNotificationsPage />
                )}
              />
              <Route
                path={RoutePaths.PUSH_NOTIFICATION_DETAILS}
                element={requirePermission(
                  UserPermisssions.VIEW_CAMPAIGNS,
                  <PushNotificationDetailsPage />
                )}
              />
              <Route
                path={RoutePaths.PUSH_NOTIFICATION_CREATE}
                element={requirePermission(
                  UserPermisssions.CREATE_CAMPAIGN,
                  <PushNotificationDetailsPage />
                )}
              />
              <Route path={RoutePaths.ALL} element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default PrivateLayout;
